import * as Functions from '../functions/common';
import * as Sentry from '@sentry/react';

import {
    AddendumComparisonDto,
    ApiResponse,
    ConceptTotalsDto,
    ContractStatisticsFilter,
    ContractStatisticsPagingFilter,
    PagedApiResponse,
    PatientTotalsDto,
    StatisticsSummaryDto,
} from '../models/Entities/Contracts/Statistics/ContractStatisticsModels';

const BaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'contracts/';

const buildQueryString = (filter?: ContractStatisticsFilter): string => {
    const queryParams = new URLSearchParams();

    if (filter?.addendumId) {
        queryParams.set('addendumId', String(filter.addendumId));
    }

    if (filter?.from) {
        queryParams.set('from', filter.from);
    }

    if (filter?.to) {
        queryParams.set('to', filter.to);
    }

    const qs = queryParams.toString();
    return qs ? `?${qs}` : '';
};

const withPaging = (filter?: ContractStatisticsPagingFilter): string => {
    const queryParams = new URLSearchParams();

    if (filter?.addendumId) {
        queryParams.set('addendumId', String(filter.addendumId));
    }

    if (filter?.from) {
        queryParams.set('from', filter.from);
    }

    if (filter?.to) {
        queryParams.set('to', filter.to);
    }

    if (filter?.page) {
        queryParams.set('page', String(filter.page));
    }

    if (filter?.pageSize) {
        queryParams.set('pageSize', String(filter.pageSize));
    }

    const qs = queryParams.toString();
    return qs ? `?${qs}` : '';
};

const parseResponse = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
        Sentry.captureException(response);
        const body = await response.text();
        throw Functions.HttpErrorHandler(body, response);
    }

    return response.json() as Promise<T>;
};

const assertSucceeded = <T>(result: ApiResponse<T> | PagedApiResponse<T>): void => {
    if (result.succeeded) {
        return;
    }

    const firstError = result.errors?.[0];
    const error = new Error(result.message || firstError || 'No se pudieron obtener las estadísticas del contrato.');
    (error as any).ErrorCode = 400;
    (error as any).ErrorMessage = error.message;
    (error as any).Errors = result.errors || [];
    throw error;
};

export const GetSummary = async (contractId: number, token: string): Promise<StatisticsSummaryDto | null> => {
    const requestUrl = `${BaseURL}${contractId}/stats/summary`;

    try {
        const result = await fetch(requestUrl, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => parseResponse<ApiResponse<StatisticsSummaryDto>>(response));

        assertSucceeded(result);
        return result.data;
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
};

export const GetTotalsByConcept = async (
    contractId: number,
    token: string,
    filter?: ContractStatisticsFilter
): Promise<ConceptTotalsDto[]> => {
    const requestUrl = `${BaseURL}${contractId}/stats/total-by-concept${buildQueryString(filter)}`;

    try {
        const result = await fetch(requestUrl, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => parseResponse<ApiResponse<ConceptTotalsDto[]>>(response));

        assertSucceeded(result);
        return result.data || [];
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
};

export const GetTotalsByConceptDetailed = async (
    contractId: number,
    token: string,
    filter?: ContractStatisticsFilter
): Promise<ConceptTotalsDto[]> => {
    const requestUrl = `${BaseURL}${contractId}/stats/total-by-concept-detailed${buildQueryString(filter)}`;

    try {
        const result = await fetch(requestUrl, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => parseResponse<ApiResponse<ConceptTotalsDto[]>>(response));

        assertSucceeded(result);
        return result.data || [];
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
};

export const GetTotalsByPatient = async (
    contractId: number,
    token: string,
    filter?: ContractStatisticsPagingFilter
): Promise<PagedApiResponse<PatientTotalsDto>> => {
    const requestUrl = `${BaseURL}${contractId}/stats/total-by-patient${withPaging(filter)}`;

    try {
        const result = await fetch(requestUrl, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => parseResponse<PagedApiResponse<PatientTotalsDto>>(response));

        assertSucceeded(result);
        return {
            ...result,
            data: result.data || [],
        };
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
};

export const GetCompareByAddendum = async (
    contractId: number,
    token: string,
    addendumIds?: number[]
): Promise<AddendumComparisonDto[]> => {
    const params = new URLSearchParams();

    if (addendumIds && addendumIds.length > 0) {
        params.set('addendumIds', addendumIds.join(','));
    }

    const query = params.toString();
    const querySuffix = query ? `?${query}` : '';
    const requestUrl = `${BaseURL}${contractId}/stats/compare-by-addendum${querySuffix}`;

    try {
        const result = await fetch(requestUrl, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => parseResponse<ApiResponse<AddendumComparisonDto[]>>(response));

        assertSucceeded(result);
        return result.data || [];
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
};
